import {
  upperFirst,
  removeLeadingWhitespace,
  removeTrailingWhitespace
} from "./string"
import {
  ERRORS,
  FORM_ERRORS
} from "../constants/errors"

const isValidEmail = (email) => {
  // Thanks to:
  // http://fightingforalostcause.net/misc/2006/compare-email-regex.php
  // http://thedailywtf.com/Articles/Validating_Email_Addresses.aspx
  // http://stackoverflow.com/questions/201323/what-is-the-best-regular-expression-for-validating-email-addresses/201378#201378
  // https://github.com/Sembiance/email-validator
  const tester = /^[-!#$%&'*+\/0-9=?A-Z^_a-z{|}~](\.?[-!#$%&'*+\/0-9=?A-Z^_a-z`{|}~])*@[a-zA-Z0-9](-?\.?[a-zA-Z0-9])*\.[a-zA-Z](-?[a-zA-Z0-9])+$/
  if (!email) {
    return false
  }

  if (email.length > 254) {
    return false
  }

  const valid = tester.test(email)
  if (!valid) {
    return false
  }
  // Further checking of some things regex can't handle
  const parts = email.split("@")
  if (parts[0].length > 64) {
    return false
  }

  const domainParts = parts[1].split(".")
  if (domainParts.some(part => part.length > 63)) {
    return false
  }
  return true
}

/**
 * Test for no special characters other than space, hyphen and apostrophe.
 * @param {String} string - String to test
 * @returns {Boolean} - Returns true if no special characters other than space, hyphen and apostrophe are found.
 */
const limitedSpecialCharacters = string => !string.match(/([^\w\- ']|_)/gi)

/**
 * Strict test for special characters. Checks for limitedSpecialCharacters
 * then checks that permitted special characters are not found more than once.
 * @param {String} string - String to test
 * @returns {Boolean} - Returns true if no special characters and that there isn't more than 1 permitted special character.
 */
const strictSpecialCharacters = (string) => {
  const match = string.match(/('|-|\.| )/gi) || []
  return !limitedSpecialCharacters(string) || match.length > 1
}

/**
 * Test for whitespace at beginning of string
 * @param {String} string - String to test
 * @returns {String} - Returns true if string begins with whitespace
 */
const beginsWithWhitespace = string => string.match(/^\s+/i)

/**
 * Test for whitespace at the end of string
 * @param {String} string - String to test
 * @returns {String} - Returns true if string ends with whitespace
 */
const endsWithWhitespace = string => string.match(/\s+$/i)

/**
 * Test for special character at the start of string
 * @param {String} string - String to test
 * @returns {String} - Returns true if string begins with a special character.
 */
const beginsWithSpecial = string => !string.match(/^[A-Za-z]/i)

/**
 * Tests for special character at the end of string
 * @param {String} string - String to test
 * @returns {String} - Returns true if string ends with special character
 */
const endsWithSpecial = string => !string.match(/[A-Za-z]$/i)

/**
 * Test for capital at beginning of string.
 * @param {String} string - String to test
 * @returns {Boolean} - Returns true if string begins with capital letter.
 */
const beginsWithCapital = (string) => {
  const test = /^[A-Z]/
  return Boolean(string.match(test))
}

/**
 * Test for numbers in string
 * @param {String} string - String to test
 * @returns {Boolean} - Returns true string contains numbers.
 */
const containsNumbers = (string) => {
  const test = /[0-9]/
  return Boolean(string.match(test))
}

/**
 * Test for valid New Zealand area code
 * @param {String} string - String to test
 * @returns {Boolean} - Returns true for valid NewZealand area codes
 */
const NewZealandAreaCode = (string) => {
  const test = /^0(2|3|4|7|8|9)\d+$/
  const subject = typeof string === "string" ? string : String(string)
  return Boolean(subject.match(test))
}

const NewZealandMobileNumber = (string) => {
  const test = /^04\d+$/
  const subject = typeof string === "string" ? string : String(string)
  return Boolean(subject.match(test))
}

export const hasLowerUpperCase = (string) => {
  const test = new RegExp("^(?=.*[a-z])(?=.*[A-Z]).+$")
  const subject = typeof string === "string" ? string : String(string)
  return Boolean(test.exec(subject))
}

export const lengthCheck = (string, min, max) => {
  const subject = typeof string === "string" ? string : String(string)
  return Boolean(subject !== "undefined" && subject.length >= min && subject.length <= max)
}

export const hasNumber = (string) => {
  const test = /^(?=.*\d).+$/
  const subject = typeof string === "string" ? string : String(string)
  return Boolean(subject.match(test))
}

export const hasSymbol = (string) => {
  const test = /^(?=.*(_|[^\w])).+$/
  const subject = typeof string === "string" ? string : String(string)
  return Boolean(subject.match(test))
}

/**
 * Clears whitespace before and after value.
 * Trailing whitespace will only be cleared when context is onBlur
 * @param {String} value - Input string to sanitize
 * @param {Object} context - Validation context
 * @returns {String} - Returns true if string ends with whitespace
 */
const sanitizeValue = (value, {
  onBlur
}) => {
  if (beginsWithWhitespace(value)) {
    value = removeLeadingWhitespace(value)
  }
  if (onBlur && endsWithWhitespace(value)) {
    value = removeTrailingWhitespace(value)
  }
  return value
}

export const optional = validation => (value) => {
  validation = typeof validation === "undefined" ? () => ({
    isValid: true
  }) : validation
  const result = validation(value)
  const {
    status,
    attributes
  } = result
  if (!value) {
    return {
      status: {
        isValid: true
      }
    }
  }
  return {
    status: status || result,
    attributes
  }
}
export const required = (validation, message, validationOptions) => (value, context) => {
  validation = typeof validation === "undefined" ? () => ({
    isValid: true
  }) : validation
  const result = validation(value, context, validationOptions)
  const {
    status,
    attributes
  } = result
  const requiredAttributes = {
    required: true
  }
  if (!value) {
    return {
      status: {
        isValid: false,
        message
      },
      attributes: {
        ...attributes,
        ...requiredAttributes
      }
    }
  }
  return {
    status: status || result,
    attributes: {
      ...attributes,
      ...requiredAttributes
    }
  }
}

export const validateName = v => ({
  isValid: true
})

export const validateDealersField = value => ({
  isValid: Boolean(value && value.code)
})

export const validateVinRego = (value, {
  onBlur
} = {}) => {
  const validVin = value => value.match(/^[0-9a-zA-Z]*$/)
  let isValid = true
  let message
  if (value) {
    value = sanitizeValue(value, {
      onBlur
    })

    if (!validVin(value)) {
      isValid = false
      message = FORM_ERRORS.REGO_OR_VIN_REQUIRED
    }
  }
  return {
    status: {
      modifiedValue: value,
      isValid,
      message
    },
    attributes: {
      maxLength: 17
    }
  }
}

/**
 * Firstname field validation.
 * Only special characters allowed are space, hyphen and apostrophe;
 * first letter must be capitalised;
 * Shouldn't be longer than 30 characters
 */
export const validateFirstName = (value, {
  onBlur
} = {}) => {
  let isValid = true
  let message

  if (!value) {
    isValid = false
  } else {
    value = sanitizeValue(value, {
      onBlur
    })
    if (!beginsWithCapital(value)) {
      value = upperFirst(value)
    }
    if (value.length > 30) {
      isValid = false
      message = FORM_ERRORS.FIRSTNAME_MAXLENGTH
    }
    if (beginsWithSpecial(value) || endsWithSpecial(value)) {
      isValid = false
      message = FORM_ERRORS.FIRSTNAME_SPECIAL_CHARACTERS_POSITION
    }
    if (strictSpecialCharacters(value)) {
      isValid = false
      message = FORM_ERRORS.FIRSTNAME_SPECIAL_CHARACTERS
    }
    if (containsNumbers(value)) {
      isValid = false
      message = FORM_ERRORS.FIRSTNAME_NUMBERS
    }
  }

  return {
    status: {
      modifiedValue: value,
      isValid,
      message
    },
    attributes: {
      maxLength: 30
    }
  }
}

export const validateCaptcha = (value) => {
  const isValid = value !== null
  return {
    isValid
  }
}

export const validateTime = (value) => {
  const isValid = value && value.match && value.match(/^[01]?[0-9]\/[0-5][0-9]$/)
  return {
    isValid: !!isValid,
    message: isValid ? undefined : FORM_ERRORS.TIME_INVALID
  }
}

export const validateOdometer = (value) => {
  const number = +value
  const positive = number > 0
  const integer = value && value.indexOf(".") === -1
  const isValid = positive && integer
  return {
    isValid,
    message: isValid ? undefined : FORM_ERRORS.ODOMETER_INVALID
  }
}

/**
 * PostcodeSuggest / Location field validation.
 * User must select a suggestion (populating postcode, state and suburb).
 * Sometimes Google Map doesn't return a suburb name, so we only validate
 * postcode and state before throwing error.
 */
export const validatePostcodeSuggest = (value) => {
  if (value && !(value.postcode)) {
    return {
      isValid: false,
      message: FORM_ERRORS.LOCATION_INVALID
    }
  }
  return {
    isValid: true
  }
}

/**
 * AddressSuggest / Location field validation.
 * User must select a suggestion (populating address, postcode, state and suburb).
 * Sometimes Google Map doesn't return a suburb name, so we only validate
 * postcode and state before throwing error.
 */
export const validateAddressSuggest = value => {
  if (value && !(value.number && value.postcode && value.suburb && value.address)) {
    return {
      isValid: false,
      message: FORM_ERRORS.ADDRESS_LOCATION_INVALID
    }
  }
  return {
    isValid: true
  }
}

/**
 * LastName field validation.
 * Only special characters allowed are space, hyphen and apostrophe;
 * first letter must be capitalised;
 * Shouldn't be longer than 50 characters
 */
export const validateLastName = (value, {
  onBlur
} = {}) => {
  let isValid = true
  let message
  if (!value) {
    isValid = false
  } else {
    value = sanitizeValue(value, {
      onBlur
    })

    if (!beginsWithCapital(value)) {
      value = upperFirst(value)
    }
    if (value.length > 50) {
      isValid = false
      message = FORM_ERRORS.LASTNAME_MAXLENGTH
    }
    if (beginsWithSpecial(value) || endsWithSpecial(value)) {
      isValid = false
      message = FORM_ERRORS.LASTNAME_SPECIAL_CHARACTERS_POSITION
    }
    if (strictSpecialCharacters(value)) {
      isValid = false
      message = FORM_ERRORS.LASTNAME_SPECIAL_CHARACTERS
    }
    if (containsNumbers(value)) {
      isValid = false
      message = FORM_ERRORS.LASTNAME_NUMBERS
    }
  }

  return {
    status: {
      modifiedValue: value,
      isValid,
      message
    },
    attributes: {
      maxLength: 50
    }
  }
}
/**
 * Phone number field validation.
 * Valid landline or mobile phone number by prefix i.e. 02, 03, 04, etc (i.e New Zealand phone numbers);
 * This field will be used by MA IT dedup tool to populate the Mobile Phone/Home Phone fields in the Prospect DB;
 */
export const validatePhoneNumber = (value) => {
  let isValid = true
  let message
  if (!value) {
    isValid = false
  } else {
    if (value.length < 9) {
      isValid = false
      message = FORM_ERRORS.PHONE_NUMBER_INVALID
    }
    if (!NewZealandAreaCode(value)) {
      isValid = false
      message = FORM_ERRORS.PHONE_NUMBER_AREACODE_INVALID
    }
  }
  return {
    status: {
      isValid,
      message
    },
    attributes: {
      maxLength: 11
    }
  }
}

export const validateMobileNumber = (value) => {
  let isValid = true
  let message
  if (!value) {
    isValid = false
  } else {
    if (value.length < 9) {
      isValid = false
      message = FORM_ERRORS.MOBILE_NUMBER_INVALID
    }
    // if (!NewZealandMobileNumber(value)) {
    //   isValid = false
    //   message = FORM_ERRORS.MOBILE_NUMBER_INVALID
    // }
  }
  return {
    status: {
      isValid,
      message
    },
    attributes: {
      maxLength: 11
    }
  }
}

export const validatePhoneExtension = (value) => {
  let isValid = true
  let message
  if (!value) {
    isValid = false
  } else if (!value.match(/\[?([0-9])\]?/g)) {
    isValid = false
    message = FORM_ERRORS.PHONE_EXTENSION_INVALID
  }
  return {
    status: {
      isValid,
      message
    },
    attributes: {
      maxLength: 6
    }
  }
}

export const validateVin = (v) => {
  v = (`${v}`).toUpperCase()
  const transliterate = c => "0123456789.ABCDEFGH..JKLMN.P.R..STUVWXYZ".indexOf(c) % 10
  const checkDigit = (vin) => {
    const map = "0123456789X"
    const weights = "8765432X098765432"
    let sum = 0
    for (let i = 0; i < 17; ++i) {
      sum += transliterate(vin[i]) * map.indexOf(weights[i])
    }
    return map[sum % 11]
  }
  // https://en.wikipedia.org/wiki/Vehicle_identification_number#Check-digit_calculation
  // but it seems that check digit not used in New Zealand
  // const isValid = (v.length === 17) && v[8] === checkDigit(v)
  const isValid = Boolean(/[A-HJ-NPR-Z0-9]{13}[0-9]{4}/.exec(v))
  return {
    isValid,
    message: isValid ? undefined : FORM_ERRORS.VIN_INVALID
  }
}

export const validateRego = (v) => {
  const isValid = Boolean(/[A-Za-z0-9]{1,6}/.exec(v))
  return {
    isValid,
    message: isValid ? undefined : FORM_ERRORS.REGISTRATION_INVALID
  }
}

export const validateEmail = (value, {
  onBlur
} = {}) => {
  let isValid = true
  let message

  if (!value) {
    isValid = false
  } else {
    value = sanitizeValue(value, {
      onBlur
    })
    if (value.length > 60) {
      isValid = false
      message = FORM_ERRORS.EMAIL_MAXLENGTH
    }
    if (!isValidEmail(value)) {
      isValid = false
      message = FORM_ERRORS.EMAIL_INVALID
    }
  }

  return {
    status: {
      modifiedValue: value,
      isValid,
      message
    },
    attributes: {
      maxLength: 60
    }
  }
}

export const postcodeValidation = (value) => {
  const test = new RegExp("^\\d{4}$")
  const isValid = value.length <= 4 && !!test.exec(value)
  return {
    isValid,
    message: isValid ? undefined : ERRORS.POSTCODE_INVALID
  }
}

export const validatePassword = (value) => {
  let isValid = true
  let message

  if (!value) {
    isValid = false
  } else {
    if (value.length < 8) {
      isValid = false
      message = FORM_ERRORS.PASSWORD_MINLENGTH
    }
    if (value.length > 20) {
      isValid = false
      message = FORM_ERRORS.PASSWORD_MAXLENGTH
    }
    if (!hasLowerUpperCase(value)) {
      isValid = false
      message = FORM_ERRORS.PASSWORD_CASE
    }
    if (!(hasNumber(value) || hasSymbol(value))) {
      isValid = false
      message = FORM_ERRORS.PASSWORD_NUMBER_SYMBOL
    }
  }

  return {
    status: {
      modifiedValue: value,
      isValid,
      message
    },
    attributes: {
      minLength: 8
    }
  }
}



// WEBPACK FOOTER //
// ./src/utils/validation.js